export const BATTERY_STATUS = Object.freeze({
    NONE: "NONE",
    LOW: "LOW",
    MEDIUM: "MEDIUM",
    HIGH: "HIGH",
} as const);

export const DEVICE_TYPES = Object.freeze({
    GATEWAY: "GATEWAY",
    THERMOSTAT: "THERMOSTAT", // Vicky
    WINDOW_CONTACT: "WINDOW_CONTACT",
    WALL_THERMOSTAT: "WALL_THERMOSTAT",
    TEMPERATURE_SENSOR: "TEMPERATURE_SENSOR",
    HEADLESS_RADIATOR_THERMOSTAT: "HEADLESS_RADIATOR_THERMOSTAT", // ELV-Thermostat without display
    FAN_COIL_THERMOSTAT: "FAN_COIL_THERMOSTAT",
    HEAT_METER: "HEAT_METER",
    SWITCH: "SWITCH",
} as const);

// devices that can set temperature
export const TEMPERATURE_CONTROLLER_DEVICE_TYPES = [
    DEVICE_TYPES.THERMOSTAT,
    DEVICE_TYPES.WALL_THERMOSTAT,
    DEVICE_TYPES.FAN_COIL_THERMOSTAT,
    DEVICE_TYPES.HEADLESS_RADIATOR_THERMOSTAT,
];

// devices that can show temperature
export const TEMPERATURE_SENSOR_DEVICE_TYPES = [
    DEVICE_TYPES.TEMPERATURE_SENSOR,
    DEVICE_TYPES.WALL_THERMOSTAT,
    DEVICE_TYPES.THERMOSTAT,
    DEVICE_TYPES.HEADLESS_RADIATOR_THERMOSTAT,
    DEVICE_TYPES.FAN_COIL_THERMOSTAT,
];

// thermostats that can control radiators
export const RADIATOR_THERMOSTAT_TYPES = [
    DEVICE_TYPES.THERMOSTAT,
    DEVICE_TYPES.WALL_THERMOSTAT,
    DEVICE_TYPES.HEADLESS_RADIATOR_THERMOSTAT,
];

export const DEVICE_IDENTIFIER = Object.freeze({
    SERIALNUMBER: "serialNumber",
    DEVEUI: "lorawanData.devEui",
} as const);

export const DEVICE_ERROR_NOTIFICATIONS = Object.freeze({
    ADAPTATION_FAULT: "ADAPTATION_FAULT",
    BROKEN_SENSOR: "BROKEN_SENSOR",
    DEVICE_UNREACHABLE: "DEVICE_UNREACHABLE",
    LOW_BATTERY: "LOW_BATTERY",
    TARGET_TEMPERATURE_RETRY_FAULT: "TARGET_TEMPERATURE_RETRY_FAULT",
    NOT_YET_ADAPTED: "NOT_YET_ADAPTED", // 0x01
    ADAPTATION_FAULT_ETRV: "ADAPTATION_FAULT_ETRV", // 0x05
    ADJUSTMENT_RANGE_TOO_LARGE: "ADJUSTMENT_RANGE_TOO_LARGE", // 0x03
    BLOCKED_VALVE: "BLOCKED_VALVE", // 0x02 or 0x04
    DETACHED_BACKPLATE: "DETACHED_BACKPLATE",
} as const);

export const DEVICE_UNREACHABLE_TYPE = Object.freeze({
    GATEWAY: "DEVICE_UNREACHABLE_GATEWAY",
    OTHER: "DEVICE_UNREACHABLE_OTHER",
} as const);

export const DEVICE_FILTERS = Object.freeze({
    UNASSIGNED: "unassigned",
    RETURNED: "returned",
});

export const CONNECTION_STATUS = Object.freeze({
    SF: "SF",
    OFFLINE: "OFFLINE",
    ONLINE: "ONLINE",
    NOT_USED: "NOT_USED",
    IN_RETURN: "IN_RETURN",
} as const);

export const AVAILABILITY_STATUS = Object.freeze({
    OFFLINE: "OFFLINE",
    ONLINE: "ONLINE",
    RETURNED: "RETURNED",
    UNUSED: "UNUSED",
} as const);

export const RELAY_STATE = Object.freeze({
    ON: "ON",
    OFF: "OFF",
} as const);

export const GATEWAY_STATUS_SOURCE = Object.freeze({
    LNS: "LNS",
    IOT_HUB: "IOT_HUB",
} as const);

export const START_LINE_CSV = 1;

export const UPLINK_INTERVALS_SECONDS = [
    300, // 5 Min.
    600, // 10 Min.
    900, // 15 Min.
    1800, // 30 Min.
    3600, // 60 Min.
    7200, // 2 Hours
    21600, // 6 Hours
    36000, // 10 Hours
    50400, // 14 Hours
    64800, // 18 Hours
    86400, // 24 Hours
    172800, // 48 Hours
];

export const INVALID_DEVICE_CODES = Object.freeze({
    DUPLICATE: "DUPLICATE",
    MISSING_VALUE: "MISSING_VALUE",
    WRONG_VALUE: "WRONG_VALUE",
    EXISTING: "EXISTING",
} as const);

// modes that can be active on a device or space/subspace
export const OPERATION_MODES = Object.freeze({
    HEATING: "HEATING",
    COOLING: "COOLING",
    VENTILATION: "VENTILATION",
    CONTROL_ONLY: "CONTROL_ONLY",
    ON: "ON",
    OFF: "OFF",
});

// modes that can be set on fan coil devices
export const FAN_COIL_OPERATION_MODES = Object.freeze({
    COOLING_VENTILATION: "COOLING_VENTILATION",
    HEATING_VENTILATION: "HEATING_VENTILATION",
    HEATING_COOLING_VENTILATION: "HEATING_COOLING_VENTILATION",
    AUTOMATIC_CHANGEOVER: "AUTOMATIC_CHANGEOVER",
    CONTROL_ONLY: "CONTROL_ONLY",
    OFF: "OFF",
});

// modes that can be set on radiator thermostat devices
export const RADIATOR_OPERATION_MODES = Object.freeze({
    ON: "ON",
    OFF: "OFF",
});

export const VALVE_CONTROL_MODES = Object.freeze({
    MANUAL_VALVE_POSITION: "MANUAL_VALVE_POSITION",
    AUTOMATIC_TEMPERATURE: "AUTOMATIC_TEMPERATURE",
    TEMPERATURE_SCHEDULE: "TEMPERATURE_SCHEDULE",
    AUTOMATIC_WITH_EXTERNAL_TEMPERATURE: "AUTOMATIC_WITH_EXTERNAL_TEMPERATURE",
});

export const FAN_COIL_APPLICATION_CODES = Object.freeze({
    APP_00: "00",
    APP_01: "01",
    APP_02: "02",
    APP_03: "03",
    APP_04: "04",
    APP_05: "05",
} as const);

export const FAN_COIL_FAN_TYPES = Object.freeze({
    SPEED_3: "3_SPEED",
    ECM: "ECM",
} as const);

export const FAN_COIL_PIPE_SYSTEMS = Object.freeze({
    PIPE_2: "2_PIPE",
    PIPE_4: "4_PIPE",
} as const);

export const FAN_COIL_WIRING = {
    [FAN_COIL_APPLICATION_CODES.APP_00]: {
        FAN_SPEED: FAN_COIL_FAN_TYPES.SPEED_3,
        PIPE_SYSTEM: FAN_COIL_PIPE_SYSTEMS.PIPE_2,
    },
    [FAN_COIL_APPLICATION_CODES.APP_01]: {
        FAN_SPEED: FAN_COIL_FAN_TYPES.SPEED_3,
        PIPE_SYSTEM: FAN_COIL_PIPE_SYSTEMS.PIPE_2,
    },
    [FAN_COIL_APPLICATION_CODES.APP_02]: {
        FAN_SPEED: FAN_COIL_FAN_TYPES.SPEED_3,
        PIPE_SYSTEM: FAN_COIL_PIPE_SYSTEMS.PIPE_4,
    },
    [FAN_COIL_APPLICATION_CODES.APP_03]: {
        FAN_SPEED: FAN_COIL_FAN_TYPES.ECM,
        PIPE_SYSTEM: FAN_COIL_PIPE_SYSTEMS.PIPE_4,
    },
    [FAN_COIL_APPLICATION_CODES.APP_04]: {
        FAN_SPEED: FAN_COIL_FAN_TYPES.ECM,
        PIPE_SYSTEM: FAN_COIL_PIPE_SYSTEMS.PIPE_2,
    },
    [FAN_COIL_APPLICATION_CODES.APP_05]: {
        FAN_SPEED: FAN_COIL_FAN_TYPES.ECM,
        PIPE_SYSTEM: FAN_COIL_PIPE_SYSTEMS.PIPE_2,
    },
};
export const IDENTIFIER_TYPES = Object.freeze({
    DEV_EUI: "DEV_EUI",
    SERIAL_NUMBER: "SERIAL_NUMBER",
});

export const FIRMWARE_UPDATE_STATUS = {
    UNKNOWN: "UNKNOWN", // no firmware-version available for the device
    UPDATE_AVAILABLE: "UPDATE_AVAILABLE", // when mclimate returns completed and device is not up-to-date
    UPDATING: "UPDATING",
    COMPLETED: "COMPLETED", // when mclimate returns completed and device is up-todate
    FAILED: "FAILED",
    UP_TO_DATE: "UP_TO_DATE",
    PENDING: "PENDING",
};

export const DEVICE_AVAILABILITY_STATUS = {
    ONLINE: "ONLINE",
    OFFLINE: "OFFLINE",
    RETURNED: "RETURNED",
    UNUSED: "UNUSED",
};

export const DEVICE_ACTIVITY_TYPES = {
    TELEMETRY_RECEIVED: "TELEMETRY_RECEIVED",
    COMMAND_TRANSFERED: "COMMAND_TRANSFERED",
    COMMAND_QUEUED: "COMMAND_QUEUED",
    COMMAND_SENT: "COMMAND_SENT",
    COMMAND_CONFIRMED: "COMMAND_CONFIRMED",
};

export const DEVICE_CALIBRATION_TRIGGERS = {
    SYSTEM: "SYSTEM",
    USER: "USER",
};

export const DEVICE_BRANDS = {
    MCLIMATE: "mclimate",
    MILESIGHT: "milesight",
    DRAGINO: "dragino",
    DNT: "dnt",
    ZENNER: "zenner",
    RAK: "rak",
};

export const DEVICE_MODELS = {
    VICKI: "vicki",
    MC_HT: "mc-ht",
    MC_LW_16ASPM_W_01: "mc-lw-16aspm-w-01",
    WS301: "ws301",
    LHT52: "lht52",
    MC_LW_WT_01: "mc-lw-wt-01",
    MC_LW_FCT_01: "mc-lw-fct-01",
    DNT_LW_ETRV: "dnt-lw-etrv",
    DNT_LW_ETRV_C: "dnt-lw-etrv-c",
    DNT_LW_WTH: "dnt-lw-wth",
    DNT_LW_ESI: "dnt-lw-esi",
    WT101: "wt101",
    MIL_EM300_TH: "mil-em300-th",
    HM_ZELSIUS: "hm-zelsius",
    UG65: "UG65",
    RAK: "rak",
};

export const UPLINK_TYPES = {
    CONFIRMED: "CONFIRMED",
    UNCONFIRMED: "UNCONFIRMED",
};

export const DEVICES_SEARCH_CATEGORY = Object.freeze({
    SPACE: "SPACE",
    SERIAL_NUMBER: "SERIAL_NUMBER",
    DEV_EUI: "DEV_EUI",
    DESCRIPTION: "DESCRIPTION",
} as const);

export const DEVICES_TABLE_SORT_TYPE = {
    BATTERY: "BATTERY",
    CHILDLOCK: "CHILDLOCK",
    DESCRIPTION: "DESCRIPTION",
    DEVICE_TYPE: "DEVICE_TYPE",
    INFO: "INFO",
    MAXIMUM_VALVE_OPENING: "MAXIMUM_VALVE_OPENING",
    OFFSET: "OFFSET",
    SPACE: "SPACE",
    STATUS: "STATUS",
    UPLINK: "UPLINK",
    VERSION: "VERSION",
};

export const DEVICES_FILTER_TYPE = {
    BATTERY: "BATTERY",
    CONFIGURATION: "CONFIGURATION",
    DEVICE_TYPE: "DEVICE_TYPE",
    ERROR: "ERROR",
    STATUS: "STATUS",
    UPLINK_INTERVAL: "UPLINK_INTERVAL",
    VERSION: "VERSION",
};

export const DEVICE_DISPLAY_SETTINGS_ROTATION = Object.freeze({
    UP: "UP",
    DOWN: "DOWN",
    LEFT: "LEFT",
    RIGHT: "RIGHT",
});

export const DEVICE_DISPLAY_SETTINGS_TEXTCOLOR = Object.freeze({
    DARK: "DARK",
    LIGHT: "LIGHT",
});

export const DEVICE_DISPLAY_SETTINGS_SCALE = Object.freeze({
    TEMPERATURE: "TEMPERATURE",
    NUMBER: "NUMBER",
});

export const DEVICE_TABLE_CONFIGURATION_DEFAULTS = Object.freeze({
    OFFSET: 0,
    MAXVALVE: 100,
});
